import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * useElevenLabsTTS — single-audio, ElevenLabs-only TTS hook.
 *
 * Guarantees:
 * 1. Only ONE audio element speaks at a time (per hook instance and globally).
 * 2. Same text is never re-fetched — cached blob URLs in module-level Map.
 * 3. Any prior playback (browser TTS OR audio element) is stopped before speaking again.
 * 4. NEVER falls back to window.speechSynthesis — pure ElevenLabs.
 */

// Module-level singleton to enforce "one audio at a time" across the whole app
let _currentAudio = null;
let _currentTextKey = null;
const _audioCache = new Map(); // key -> objectUrl
const _MAX_CACHE = 100;

const _hardStopAll = () => {
  if (_currentAudio) {
    try {
      _currentAudio.pause();
      _currentAudio.currentTime = 0;
    } catch {}
    _currentAudio = null;
    _currentTextKey = null;
  }
  // Kill any residual browser TTS just in case some legacy code fires it
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
};

export const useElevenLabsTTS = ({ voice = 'nova', speed = 0.95 } = {}) => {
  const { api } = useAuth();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const localAudioRef = useRef(null);
  const inFlightRef = useRef(null);

  // Read parent-configured narrator preferences from localStorage — set via
  // Settings → Grown-up Controls. These take priority over hook defaults.
  const readPrefs = () => {
    try {
      const raw = localStorage.getItem('narratorPrefs');
      if (!raw) return {};
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  };

  const stop = useCallback(() => {
    _hardStopAll();
    localAudioRef.current = null;
    setIsSpeaking(false);
  }, []);

  const speak = useCallback(async (text, opts = {}) => {
    if (!text || !text.trim()) return;
    const cleanText = text.substring(0, 4000);
    const prefs = readPrefs();
    const effVoice = opts.voice || prefs.voice || voice;
    const effSpeed = Number(opts.speed || prefs.speed || speed);
    const effVoiceId = (opts.voiceId || prefs.voice_id || '').trim();
    const key = `${cleanText}::${effVoice}::${effSpeed}::${effVoiceId}`;

    // If exact same audio is already playing → do nothing (prevents repetition on re-render)
    if (_currentTextKey === key && _currentAudio && !_currentAudio.paused) {
      return;
    }

    // Stop any prior audio globally
    _hardStopAll();

    // Debounce identical concurrent requests
    if (inFlightRef.current === key) return;
    inFlightRef.current = key;

    setIsSpeaking(true);
    try {
      let audioUrl = _audioCache.get(key);

      if (!audioUrl) {
        const params = new URLSearchParams({
          text: cleanText,
          voice: effVoice,
          speed: String(effSpeed),
        });
        if (effVoiceId) params.set('voice_id', effVoiceId);
        const response = await api.post(
          `/voice/speak?${params.toString()}`,
          {},
          { responseType: 'blob' }
        );
        audioUrl = URL.createObjectURL(response.data);

        // LRU-ish cache
        if (_audioCache.size >= _MAX_CACHE) {
          const firstKey = _audioCache.keys().next().value;
          const oldUrl = _audioCache.get(firstKey);
          try { URL.revokeObjectURL(oldUrl); } catch {}
          _audioCache.delete(firstKey);
        }
        _audioCache.set(key, audioUrl);
      }

      const audio = new Audio(audioUrl);
      localAudioRef.current = audio;
      _currentAudio = audio;
      _currentTextKey = key;

      audio.onended = () => {
        setIsSpeaking(false);
        if (_currentAudio === audio) {
          _currentAudio = null;
          _currentTextKey = null;
        }
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        if (_currentAudio === audio) {
          _currentAudio = null;
          _currentTextKey = null;
        }
      };
      await audio.play().catch(() => {
        // Autoplay blocked or other error — just release state
        setIsSpeaking(false);
      });
    } catch (err) {
      // Silent failure — do NOT fall back to browser TTS
      console.warn('ElevenLabs TTS failed:', err?.message || err);
      setIsSpeaking(false);
    } finally {
      inFlightRef.current = null;
    }
  }, [api, voice, speed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (localAudioRef.current) {
        try { localAudioRef.current.pause(); } catch {}
        if (_currentAudio === localAudioRef.current) {
          _currentAudio = null;
          _currentTextKey = null;
        }
      }
    };
  }, []);

  /**
   * speakChunks — play a sequence of short strings back-to-back and fire
   * `onIndex(currentIndex)` as each chunk starts. Used by the story reader
   * to highlight the sentence being spoken so kids can track along.
   * `onIndex(-1)` fires when playback ends or is stopped.
   */
  const chunksAbortRef = useRef(0);
  const speakChunks = useCallback(async (chunks, onIndex, opts = {}) => {
    if (!Array.isArray(chunks) || chunks.length === 0) return;
    const runId = ++chunksAbortRef.current;
    setIsSpeaking(true);
    try {
      for (let i = 0; i < chunks.length; i++) {
        if (chunksAbortRef.current !== runId) return; // stop was called
        const piece = (chunks[i] || '').trim();
        if (!piece) continue;
        if (onIndex) onIndex(i);
        // Reuse the primary speak() so caching + prefs + hardStop still apply.
        await speak(piece, opts);
        // Wait for playback to finish for THIS chunk (with a safety timeout so
        // a browser that blocked audio can't stall the whole story reader)
        const audio = localAudioRef.current;
        // Estimate duration ~ 60ms per character, minimum 1.2s, capped 15s
        const safetyMs = Math.min(15000, Math.max(1200, piece.length * 60));
        if (audio) {
          await new Promise((resolve) => {
            let done = false;
            const finish = () => { if (!done) { done = true; resolve(); } };
            audio.addEventListener('ended', finish, { once: true });
            audio.addEventListener('error', finish, { once: true });
            // Belt-and-braces: if paused externally, resolve
            if (audio.paused && audio.currentTime > 0) finish();
            setTimeout(finish, safetyMs);
          });
        } else {
          await new Promise((r) => setTimeout(r, safetyMs));
        }
        if (chunksAbortRef.current !== runId) return;
      }
    } finally {
      if (chunksAbortRef.current === runId) {
        if (onIndex) onIndex(-1);
        setIsSpeaking(false);
      }
    }
  }, [speak]);

  // Override stop() so it also cancels any in-flight chunk sequence
  const stopChunks = useCallback(() => {
    chunksAbortRef.current++;
    stop();
  }, [stop]);

  return { speak, stop: stopChunks, isSpeaking, speakChunks };
};

export default useElevenLabsTTS;
